import React, { FunctionComponent, useContext } from 'react';
import { Query } from 'react-apollo';
import { ApolloError, DocumentNode } from 'apollo-boost';
import { GraphQLError } from 'graphql';
import { Redirect } from 'react-router-dom';
import { pathOr, propOr } from 'ramda';

import { getGraphQLError } from 'helpers/getGraphQLError';
import ErrorBoundary from 'components/ErrorBoundary';
import { BatchReportContext } from 'scenes/Report/scenes/BatchReport';
import { ReportPlaceholder } from 'components/Placeholder';
import { useUserMgmt } from 'components/UserMgmt/UserMgmt.context';
import { ReportDataContext } from 'scenes/Report/scenes/BatchReport/BatchReport';

import { ReportParams } from '../ReportBase';
import { getDefaultReportError, isInvalidData } from './Query.helpers';

type Variables = { params: ReportParams };

type Props = {
  query: DocumentNode;
  variables: Variables;
  reportName: string;
  render: Function;
  queryKey?: string;
  batchKey?: string;
  propName?: string;
  checkForValidData?: boolean;
};

export type Data = {
  [key: string]: Record<string, unknown>[];
  result: Record<string, unknown>[];
};

type ResponseParams = {
  data?: Data;
  loading?: boolean;
  error?: ApolloError;
};

const renderRedirect = (
  merchantName = '',
  reportName: string,
  pathname: string,
  error?: ApolloError | GraphQLError[],
) => {
  const state = getGraphQLError(
    getDefaultReportError(merchantName, reportName),
    false,
    error,
  );

  return (
    <Redirect
      to={{
        state,
        pathname,
      }}
    />
  );
};

const renderBatchError = (
  merchantName: string,
  reportName: string,
  batchKey: string,
  batchListener: Function,
  error?: ApolloError | GraphQLError[],
) => {
  const formattedError = getGraphQLError(
    getDefaultReportError(merchantName, reportName),
    false,
    error,
  );

  batchListener('', '', batchKey, formattedError);

  return null;
};

const QueryWrapper: FunctionComponent<Props> = (props) => {
  const {
    query,
    variables,
    reportName,
    render,
    batchKey = '',
    queryKey = '',
    propName = '',
    checkForValidData = true,
  } = props;
  const batchListener = useContext(BatchReportContext);
  const reportData = useContext(ReportDataContext);

  const merchantName = pathOr('', ['params', 'merchantName'], variables);
  const { isInternalUser } = useUserMgmt();

  if (reportData) {
    return render(reportData, variables.params);
  }

  return (
    <ErrorBoundary>
      <Query query={query} variables={variables}>
        {({ loading, error, data }: ResponseParams) => {
          if (loading) {
            return <ReportPlaceholder />;
          }

          const filteredData = propOr(data, propName, data);

          if (
            error ||
            (checkForValidData &&
              isInvalidData(
                propOr(filteredData, queryKey, filteredData),
                queryKey,
              ))
          ) {
            if (batchListener) {
              return renderBatchError(
                merchantName,
                reportName,
                batchKey,
                batchListener,
                error,
              );
            }

            const pathname = isInternalUser
              ? '/reports'
              : `/${merchantName}/reports`;

            return renderRedirect(merchantName, reportName, pathname, error);
          }

          return render(data, variables.params);
        }}
      </Query>
    </ErrorBoundary>
  );
};

export default QueryWrapper;
